/**
 * Pure presentation helpers for search & history — README §7. Grouping
 * order/labels for the typeahead, the CLOSED/DISMISSED/LIVE chip and the
 * "channel · client · owner · closed date" meta line for the History page's
 * result cards. No component code, no store/ops access — mirrors the split
 * already established by src/components/rail/format.ts and
 * src/components/thread/format.ts.
 */
import type { ThreadDetail, ThreadState } from '@communicator/shared'
import type { SearchGroup, SearchGroupType, SearchResultItem } from '../../lib/ops'
import type { HistoryFilter } from '../../store'
import { CHANNEL_LABEL } from '../rail/format'
import { formatDayHmInZone } from '@communicator/shared'

/** Design order — README §7: "Company · Person · Ticket · Tech · Live comm · Closed comm · Message · Close note". */
export const SEARCH_GROUP_ORDER: readonly SearchGroupType[] = [
  'company',
  'person',
  'ticket',
  'tech',
  'live-comm',
  'closed-comm',
  'message',
  'close-note',
]

export const SEARCH_GROUP_LABEL: Record<SearchGroupType, string> = {
  company: 'Company',
  person: 'Person',
  ticket: 'Ticket',
  tech: 'Tech',
  'live-comm': 'Live comm',
  'closed-comm': 'Closed comm',
  message: 'Message',
  'close-note': 'Close note',
}

/**
 * Renders exactly the groups the server sent, reordered into the design's
 * fixed sequence — never invents a group for a type the server omitted
 * (task-19 rule 2: "show the group empty or omit it honestly"; this picks
 * omission, since nothing downstream needs to tell "not searched yet" apart
 * from "zero hits" — both read the same to the person searching). A group
 * the server sent with zero items is dropped for the identical reason.
 */
export function visibleGroups(result: { groups: SearchGroup[] } | null): SearchGroup[] {
  if (result === null) return []
  const byType = new Map(result.groups.map((g) => [g.type, g] as const))
  const ordered: SearchGroup[] = []
  for (const type of SEARCH_GROUP_ORDER) {
    const g = byType.get(type)
    if (g && g.items.length > 0) ordered.push(g)
  }
  return ordered
}

export interface FlatSearchItem {
  groupType: SearchGroupType
  item: SearchResultItem
}

/**
 * The History page shows one flat, ordered list of cards — no group
 * headers there (README §7 describes the typeahead as the thing that
 * groups by type; the page itself just lists result cards with a count).
 * Order follows `visibleGroups`, so it's still the same honest ordering.
 */
export function flattenGroups(groups: SearchGroup[]): FlatSearchItem[] {
  return groups.flatMap((g) => g.items.map((item) => ({ groupType: g.type, item })))
}

export type CommStatus = 'CLOSED' | 'DISMISSED' | 'LIVE'

/**
 * `ThreadState` (packages/shared) has no separate "dismissed" value — it's
 * 'incoming'|'active'|'scheduled'|'closed'. Dismissal is recorded on the
 * free-form `reason` column instead (docs/design-handoff/data/seed-data.json's
 * x3 record: `st: "closed", reason: "dismissed"`), so this reads that
 * column rather than inventing a fourth ThreadState.
 */
export function commStatus(state: ThreadState, reason: string): CommStatus {
  if (state !== 'closed') return 'LIVE'
  return reason === 'dismissed' ? 'DISMISSED' : 'CLOSED'
}

export type CommStatusTone = 'ok' | 'warn' | 'bad' | 'info' | 'dispatch' | 'neutral'

export const COMM_STATUS_TONE: Record<CommStatus, CommStatusTone> = {
  CLOSED: 'neutral',
  DISMISSED: 'warn',
  LIVE: 'info',
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/**
 * "29 Jul, 15:12" — matches the seed data's own closedAt copy, computed
 * from the real ISO timestamp rather than transcribed. Uses the UTC getters
 * directly (not `Intl.DateTimeFormat` with a timezone option) for the same
 * reason `components/transcript/format.ts`'s `formatOpenedAt` does: a
 * stable result regardless of the host's local clock, so this never flakes
 * across timezones in CI. Returns null for a thread that hasn't closed (no
 * `closedAt`) or an unparsable value, rather than showing "Invalid Date".
 */
export function formatClosedDate(iso: string | null, timeZone: string): string | null {
  if (!iso) return null
  return formatDayHmInZone(iso, timeZone)
}

/**
 * "channel · client · owner · closed date" — README §7's exact card meta
 * line. A thread that hasn't closed has no closed date, so that segment
 * reads "open" rather than inventing a date; a thread with no client/owner
 * reads an honest fallback rather than a blank space.
 */
export function metaLine(
  thread: Pick<ThreadDetail, 'channel' | 'client' | 'owner' | 'closedAt'>,
  timeZone: string,
): string {
  const parts = [
    CHANNEL_LABEL[thread.channel],
    thread.client ?? 'Unknown company',
    thread.owner ?? 'Unowned',
    formatClosedDate(thread.closedAt, timeZone) ?? 'open',
  ]
  return parts.join(' · ')
}

/** README §7's exact filter-chip set, in order. Values match `HistoryFilter`/`SearchScope` (the same six literals). */
export const HISTORY_FILTERS: ReadonlyArray<{ value: HistoryFilter; label: string }> = [
  { value: 'everything', label: 'Everything' },
  { value: 'closed', label: 'Closed only' },
  { value: 'live', label: 'Live only' },
  { value: 'chat', label: 'Chat' },
  { value: 'sms', label: 'SMS' },
  { value: 'calls', label: 'Calls' },
]

/** Escapes a string for safe use inside a `RegExp` — the search query is free text a person typed, not a pattern. */
function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

export interface SnippetPart {
  text: string
  match: boolean
}

/**
 * README §9: "the snippet in quotes with the match `<mark>`ed." The server's
 * search hit carries only the snippet text, not span offsets, so this
 * derives the highlight client-side from the one string the page already
 * has that named the match — the query itself — rather than inventing a
 * position. Returns the whole snippet as one unmatched part for an empty
 * query (nothing to highlight) or when the query does not literally occur in
 * this particular snippet (a hit on a DIFFERENT field of the same result,
 * e.g. the person's name rather than this message body) — an honest "no
 * highlight" rather than a wrong one.
 *
 * Split into parts rather than built as an HTML string: the snippet is real
 * message/close-note text, which can come from a customer, so this renders
 * through React's own text nodes (like `Icon`'s `label` — see that
 * component's header comment) rather than ever going through
 * `dangerouslySetInnerHTML`.
 */
export function highlightSnippet(snippet: string, query: string): SnippetPart[] {
  const q = query.trim()
  if (!q) return [{ text: snippet, match: false }]
  const parts = snippet.split(new RegExp(`(${escapeRegExp(q)})`, 'ig'))
  return parts.filter((p) => p.length > 0).map((p) => ({ text: p, match: p.toLowerCase() === q.toLowerCase() }))
}
